import { useEffect, useMemo, useState } from 'react';
import { useListJobsQuery, useListCollectionsQuery } from '../redux/services/mixgenie';
import {
  readCachedSongs, writeCachedSongs,
  readCachedCollections, writeCachedCollections,
} from './songsCache';

/**
 * Offline-first songs + collections. On mount:
 *   1. Read localStorage cache — return immediately so the UI paints
 *      with real content, not a spinner.
 *   2. Kick off RTK Query in the background (network refresh).
 *   3. When the query resolves, mirror the fresh data back to
 *      localStorage AND flow it into `songs` / `collections`.
 *   4. If the network fails, keep the cached data and expose
 *      `isOffline` so the page can show an indicator.
 *
 * Sync triggers: window focus, `online` event, and RTK Query's
 * built-in poll (every 5s while the tab is visible). Together this
 * means the user never sees a spinner after their first visit, and
 * updates flow in silently within seconds of coming back online.
 */
export function useCachedSongs(userKey: string) {
  const cached = useMemo(() => (userKey ? readCachedSongs(userKey) : null), [userKey]);
  const cachedCollections = useMemo(
    () => (userKey ? readCachedCollections(userKey) : null),
    [userKey],
  );

  const [songs, setSongs] = useState<any[]>(cached?.data || []);
  const [collections, setCollections] = useState<any[]>(cachedCollections?.data || []);
  const [lastSyncedAt, setLastSyncedAt] = useState<number>(cached?.syncedAt || 0);
  const [collectionsSyncedAt, setCollectionsSyncedAt] = useState<number>(
    cachedCollections?.syncedAt || 0,
  );

  const {
    data: jobsData,
    isError: jobsIsError,
    isFetching: jobsIsFetching,
    refetch: refetchJobs,
  } = useListJobsQuery(
    { email: userKey, limit: 100 },
    { skip: !userKey, pollingInterval: 5000 },
  );

  const {
    data: collectionsData,
    isError: collectionsIsError,
    isFetching: collectionsIsFetching,
    refetch: refetchCollections,
  } = useListCollectionsQuery(undefined, { skip: !userKey });

  // Server → cache + state whenever the RTK Query cache updates.
  useEffect(() => {
    const list = jobsData?.data;
    if (!Array.isArray(list) || !userKey) return;
    setSongs(list);
    const ts = writeCachedSongs(userKey, list);
    if (ts) setLastSyncedAt(ts);
  }, [jobsData, userKey]);

  useEffect(() => {
    if (!Array.isArray(collectionsData) || !userKey) return;
    setCollections(collectionsData);
    const ts = writeCachedCollections(userKey, collectionsData);
    if (ts) setCollectionsSyncedAt(ts);
  }, [collectionsData, userKey]);

  // Sync on window focus + network coming back online. The RTK Query
  // poll handles the tab-in-foreground case; this covers the returning-
  // from-background case, which the poll may have paused.
  useEffect(() => {
    if (!userKey) return;
    const onFocus = () => { refetchJobs(); refetchCollections(); };
    const onOnline = () => { refetchJobs(); refetchCollections(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, [userKey, refetchJobs, refetchCollections]);

  const isOffline =
    typeof navigator !== 'undefined' && navigator.onLine === false;
  const isSyncing = jobsIsFetching || collectionsIsFetching;
  const isFetchError = jobsIsError || collectionsIsError;
  // First-visit-ever = no cache AND still loading = we have literally
  // nothing to show. The page can decide to render a skeleton then;
  // otherwise it should render the (possibly-stale) cached rows.
  const isFirstLoad = !cached && songs.length === 0 && isSyncing;

  return {
    songs,
    collections,
    lastSyncedAt,
    collectionsSyncedAt,
    isSyncing,
    isFetchError,
    isOffline,
    isFirstLoad,
    refetch: () => { refetchJobs(); refetchCollections(); },
  };
}
